<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useProviderLogsStore } from 'dashboard/stores/providerLogs';

import ProviderLogsFilters from 'dashboard/components-next/ProviderLogs/ProviderLogsFilters.vue';
import ProviderLogsTable from 'dashboard/components-next/ProviderLogs/ProviderLogsTable.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const RESULTS_PER_PAGE = 25;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const providerLogsStore = useProviderLogsStore();

const inboxes = useMapGetter('inboxes/getInboxes');

const logs = computed(() => providerLogsStore.records);
const meta = computed(() => providerLogsStore.meta);
const isFetching = computed(() => providerLogsStore.uiFlags.isFetching);

const isInitializing = ref(true);

// Filters are seeded from the URL so a shared link restores the same view.
const status = ref(route.query.status || null);
const inboxId = ref(Number(route.query.inbox_id) || null);
const phone = ref(route.query.phone || null);
const currentPage = ref(Number(route.query.page) || 1);

const syncFiltersToUrl = () => {
  router.replace({
    query: {
      ...(status.value && { status: status.value }),
      ...(inboxId.value && { inbox_id: inboxId.value }),
      ...(phone.value && { phone: phone.value }),
      ...(currentPage.value > 1 && { page: currentPage.value }),
    },
  });
};

const fetchLogs = async () => {
  syncFiltersToUrl();
  try {
    await providerLogsStore.fetchLogs({
      page: currentPage.value,
      ...(status.value ? { status: status.value } : {}),
      ...(inboxId.value ? { inbox_id: inboxId.value } : {}),
      ...(phone.value ? { phone: phone.value } : {}),
    });
  } catch (error) {
    useAlert(error?.message || t('MESSAGE_LOGS_PAGE.API.ERROR_MESSAGE'));
  }
};

watch([status, inboxId, phone], () => {
  currentPage.value = 1;
  fetchLogs();
});

const onPageChange = page => {
  currentPage.value = page;
  fetchLogs();
};

onMounted(async () => {
  try {
    await Promise.all([store.dispatch('inboxes/get'), fetchLogs()]);
  } finally {
    isInitializing.value = false;
  }
});
</script>

<template>
  <div
    v-if="isInitializing"
    class="flex items-center justify-center w-full h-full bg-n-surface-1"
  >
    <Spinner :size="24" />
  </div>
  <section
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1"
  >
    <header class="px-6 pt-6 pb-4 shrink-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('MESSAGE_LOGS_PAGE.HEADER') }}
      </h1>
      <p class="mt-1 text-body-main text-n-slate-11">
        {{ t('MESSAGE_LOGS_PAGE.DESCRIPTION') }}
      </p>
      <div class="flex items-end justify-between gap-3 mt-5">
        <ProviderLogsFilters
          v-model:status="status"
          v-model:inbox-id="inboxId"
          v-model:phone="phone"
          :inboxes="inboxes"
        />
        <Button
          ghost
          slate
          size="sm"
          icon="i-lucide-refresh-cw"
          :is-loading="isFetching"
          :aria-label="t('MESSAGE_LOGS_PAGE.REFRESH')"
          :title="t('MESSAGE_LOGS_PAGE.REFRESH')"
          @click="fetchLogs"
        />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <ProviderLogsTable
        :logs="logs"
        :inboxes="inboxes"
        :loading="isFetching"
      />
    </main>
    <footer v-if="logs.length" class="sticky bottom-0 shrink-0">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="meta.totalCount"
        :items-per-page="RESULTS_PER_PAGE"
        @update:current-page="onPageChange"
      />
    </footer>
  </section>
</template>
